import React, { useEffect, useRef, useState } from 'react';
import './ScoreEntry.css';

export function displayRoundScore(value) {
  if (value === '' || value === undefined || value === null || value === '-') return '—';
  const number = Number(value);
  return number > 0 ? `+${number}` : String(number);
}

export default function ScoreEntry({ initialPlayer, players, scores, round, totalFor, renderAvatar, onSave, onSubmit, onClose }) {
  const [player, setPlayer] = useState(initialPlayer);
  const [draft, setDraft] = useState(String(scores[initialPlayer] ?? ''));
  const [replace, setReplace] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const submitted = useRef(false);
  const panel = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  useEffect(() => {
    if (!players.includes(player)) close.current();
  }, [players, player]);

  const save = () => onSave(player, draft === '-' ? '' : draft);
  const select = (next) => {
    if (next === player) return;
    save();
    setPlayer(next);
    setDraft(String(scores[next] ?? ''));
    setReplace(true);
  };
  const key = (value) => {
    if (value === 'backspace') {
      setDraft(old => old.slice(0, -1));
      setReplace(false);
    } else if (value === 'sign') {
      setDraft(old => old.startsWith('-') ? old.slice(1) : `-${old}`);
    } else {
      setDraft(old => {
        const negative = old.startsWith('-');
        let magnitude = replace ? '' : old.replace(/^-/, '');
        if (magnitude.length >= 9) return old;
        magnitude = (magnitude + value).replace(/^0+(?=\d)/, '');
        return (negative ? '-' : '') + magnitude;
      });
      setReplace(false);
    }
  };
  const roundScores = { ...scores, [player]: draft === '-' ? '' : draft };
  const difference = players.reduce((sum, name) => sum + (parseInt(roundScores[name], 10) || 0), 0);
  const submit = () => {
    if (submitted.current) return;
    const error = onSubmit(roundScores);
    if (error) { setSubmitError(error); return; }
    submitted.current = true;
    onClose();
  };
  const next = () => select(players[(players.indexOf(player) + 1) % players.length]);
  const keyboard = (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (/^\d$/.test(event.key)) { event.preventDefault(); key(event.key); }
    else if (event.key === 'Backspace') { event.preventDefault(); key('backspace'); }
    else if (event.key === '-') { event.preventDefault(); setDraft(old => '-'+old.replace(/^-/, '')); }
    else if (event.key === '+') { event.preventDefault(); setDraft(old => old.replace(/^-/, '')); }
    else if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    else if (event.key === 'Tab') {
      const buttons = [...panel.current.querySelectorAll('button:not(:disabled)')];
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
  };

  return <div className="score-entry-backdrop">
    <section className="score-entry-dialog" role="dialog" aria-modal="true" aria-labelledby="score-entry-title"
      ref={panel} tabIndex={-1} onKeyDown={keyboard}>
      <header className="score-entry-header">
        <h2 id="score-entry-title">Enter Score</h2>
        <span>Round {round}</span>
        <button onClick={() => { save(); onClose(); }} aria-label="Close score entry">×</button>
      </header>
      <nav className="score-entry-players" aria-label="Select player" style={{ '--entry-players': players.length }}>
        {players.map(name => <button key={name} onClick={() => select(name)}
          className={name === player ? 'selected' : ''} aria-pressed={name === player}
          aria-label={`Enter score for ${name}`}>
          <span className="entry-player-summary">
            <span className="entry-player-total" aria-label={`${name} current total`}>{displayRoundScore(totalFor(name))}</span>
            <span className="entry-mini-avatar">{renderAvatar(name, 36)}</span>
          </span>
          <span className="entry-player-name">{name}</span>
          <strong className={Number(name === player ? draft : scores[name]) < 0 ? 'negative' : 'positive'}>
            {displayRoundScore(name === player ? draft : scores[name])}
          </strong>
        </button>)}
      </nav>
      <small className={`score-entry-legend ${submitError ? 'entry-submit-error' : ''}`} role={submitError ? 'alert' : undefined}>
        {submitError ? (submitError.startsWith('The scores are off by') ? (difference !== 0 ? `Scores off by ${displayRoundScore(difference)} — must total 0.` : 'Scores balance — tap Submit to save the round.') : submitError) : '— Not entered'}
      </small>
      <div className="score-entry-body">
        <div className="score-entry-person">
          <div className="entry-large-avatar">{renderAvatar(player, 90)}</div>
          <h3>{player}</h3>
          <div className="entry-current-total">Current total <strong>{displayRoundScore(totalFor(player))}</strong></div>
          <label htmlFor="entry-round-value">This round</label>
          <output id="entry-round-value" className={Number(draft) > 0 ? 'round-positive' : Number(draft) < 0 ? 'round-negative' : ''} aria-label={`${player} this round`} aria-live="polite">
            {draft === '-' ? '−' : displayRoundScore(draft)}
          </output>
          <small>Not submitted yet</small>
        </div>
        <div className="score-entry-keypad-wrap">
          <div className="score-entry-keypad" aria-label="Score keypad">
            {['1','2','3','backspace','4','5','6','sign','7','8','9','0'].map(value =>
              <button key={value} onClick={() => key(value)}
                aria-label={value === 'backspace' ? 'Backspace' : value === 'sign' ? 'Toggle positive or negative' : value}>
                {value === 'backspace' ? '⌫' : value === 'sign' ? '+ / −' : value}
              </button>)}
          </div>
        </div>
      </div>
      <footer className="score-entry-actions">
        <button onClick={onClose}>Cancel</button>
        <button className="entry-done" onClick={submit}>Submit</button>
        <button onClick={next} disabled={players.length < 2}>Next Player →</button>
      </footer>
    </section>
  </div>;
}
